import {Button, Group, Image, Modal, Radio, SimpleGrid, Text, Title} from '@mantine/core';
import {toNumber} from 'lodash';
import {IPointData} from 'pixi.js';
import {useCallback, useState} from 'react';
import classes from './InitialNetworkPopup.module.css';
import imageOf18 from '../../raw-assets/network of 18.svg';
import imageOf6 from '../../raw-assets/network of 6.svg';
import imageOf9 from '../../raw-assets/network of 9.svg';
import {useAddLinkByNodeId, useAddNode} from '../state/networkHooks';
import {Link, NodeId} from '../state/networkStore';
import {useSetDesignActive, useSetInitialNetworkMade} from '../state/uiStore';

export interface PlaceholderNodes {
    [K: NodeId]: IPointData
}

export interface DesignMode {
    nodes:  PlaceholderNodes,
    links: Link[],
}

const networkOf6: DesignMode = {
    nodes: {
        1: {x: 150, y: 700},
        2: {x: 350, y: 400},
        3: {x: 550, y: 600},
        4: {x: 950, y: 600},
        5: {x: 1150, y: 400},
        6: {x: 1350, y: 700},
    },
    links: [
        {nodeIdA: 2, nodeIdB: 1, relationship: 'local'},
        {nodeIdA: 2, nodeIdB: 3, relationship: 'local'},
        {nodeIdA: 3, nodeIdB: 1, relationship: 'local'},
        {nodeIdA: 3, nodeIdB: 4, relationship: 'remote'},
        {nodeIdA: 5, nodeIdB: 4, relationship: 'local'},
        {nodeIdA: 4, nodeIdB: 6, relationship: 'local'},
        {nodeIdA: 5, nodeIdB: 6, relationship: 'local'},
    ],
};

const networkOf9: DesignMode = {
    nodes: {
        1: {x: 100, y: 750},
        2: {x: 300, y: 550},
        3: {x: 500, y: 750},
        4: {x: 625, y: 325},
        5: {x: 750, y: 200},
        6: {x: 900, y: 325},
        7: {x: 1000, y: 750},
        8: {x: 1200, y: 550},
        9: {x: 1400, y: 750},
    },
    links: [
        {nodeIdA: 2, nodeIdB: 1, relationship: 'local'},
        {nodeIdA: 2, nodeIdB: 3, relationship: 'local'},
        {nodeIdA: 3, nodeIdB: 1, relationship: 'local'},
        {nodeIdA: 4, nodeIdB: 2, relationship: 'remote'},
        {nodeIdA: 5, nodeIdB: 4, relationship: 'local'},
        {nodeIdA: 5, nodeIdB: 6, relationship: 'local'},
        {nodeIdA: 4, nodeIdB: 6, relationship: 'local'},
        {nodeIdA: 6, nodeIdB: 8, relationship: 'remote'},
        {nodeIdA: 8, nodeIdB: 7, relationship: 'local'},
        {nodeIdA: 8, nodeIdB: 9, relationship: 'local'},
        {nodeIdA: 7, nodeIdB: 9, relationship: 'local'},
    ],
};

const networkOf18: DesignMode = {
    nodes: {
        1: {x: 100, y: 750}, // FRONT LEFT
        2: {x: 225, y: 550},
        3: {x: 400, y: 725},

        4: {x: 625, y: 600}, // CENTER
        5: {x: 750, y: 425},
        6: {x: 900, y: 600},

        7: {x: 1125, y: 700}, // FRONT RIGHT
        8: {x: 1300, y: 800},
        9: {x: 1400, y: 600},

        10: {x: 350, y: 300}, // LEFT
        11: {x: 440, y: 175},
        12: {x: 550, y: 275},

        13: {x: 1050, y: 325}, // RIGHT
        14: {x: 1150, y: 225},
        15: {x: 1275, y: 350},

        16: {x: 725, y: 125}, // BACK
        17: {x: 850, y: 200},
        18: {x: 950, y: 125},

    },
    links: [
        {nodeIdA: 2, nodeIdB: 1, relationship: 'local'}, // FRONT LEFT
        {nodeIdA: 2, nodeIdB: 3, relationship: 'local'},
        {nodeIdA: 3, nodeIdB: 1, relationship: 'local'},

        {nodeIdA: 4, nodeIdB: 3, relationship: 'remote'},
        {nodeIdA: 4, nodeIdB: 5, relationship: 'local'}, // CENTER
        {nodeIdA: 6, nodeIdB: 5, relationship: 'local'},
        {nodeIdA: 4, nodeIdB: 6, relationship: 'local'},

        {nodeIdA: 6, nodeIdB: 7, relationship: 'remote'},
        {nodeIdA: 7, nodeIdB: 8, relationship: 'local'}, // FRONT RIGHT
        {nodeIdA: 9, nodeIdB: 8, relationship: 'local'},
        {nodeIdA: 9, nodeIdB: 7, relationship: 'local'},

        {nodeIdA: 10, nodeIdB: 2, relationship: 'remote'},
        {nodeIdA: 11, nodeIdB: 10, relationship: 'local'}, // LEFT
        {nodeIdA: 12, nodeIdB: 10, relationship: 'local'},
        {nodeIdA: 11, nodeIdB: 12, relationship: 'local'},

        {nodeIdA: 15, nodeIdB: 9, relationship: 'remote'},
        {nodeIdA: 14, nodeIdB: 13, relationship: 'local'}, // RIGHT
        {nodeIdA: 14, nodeIdB: 15, relationship: 'local'},
        {nodeIdA: 13, nodeIdB: 15, relationship: 'local'},

        {nodeIdA: 17, nodeIdB: 5, relationship: 'remote'},
        {nodeIdA: 18, nodeIdB: 16, relationship: 'local'}, // BACK
        {nodeIdA: 16, nodeIdB: 17, relationship: 'local'},
        {nodeIdA: 18, nodeIdB: 17, relationship: 'local'},
    ],
};
const data = [
    {name: 'Empty network', network: {nodes:{}, links:[]}},
    {name: '6 players', image: imageOf6, network: networkOf6},
    {name: '9 players', image: imageOf9, network: networkOf9},
    {name: '18 players', image: imageOf18, network: networkOf18},
];

export function InitialNetworkPopup() {
    const [value, setValue] = useState<string | null>(null);
    const setDesignActive = useSetDesignActive();
    const setInitialNetworkSelectionMade = useSetInitialNetworkMade();
    const addNode = useAddNode();
    const addLinkByNodeId = useAddLinkByNodeId();
    
    const applyDesignModeState = useCallback((networkName: string) => {
        const networkDesign = data.find(network => network.name === networkName)!;
        Object.entries(networkDesign.network.nodes).forEach(([nodeId, position]:[string, IPointData]) => addNode(
            {nodeId: toNumber(nodeId), x: position.x, y: position.y},
        ));
        networkDesign.network.links.forEach(link => addLinkByNodeId(link.nodeIdA, link.nodeIdB, link.relationship));
        setInitialNetworkSelectionMade(true);
    }, [ addNode, addLinkByNodeId]);

    function close() {
        setDesignActive(false);
        setValue(null);
    }

    const cards = data.map((item) => (
        <Radio.Card className={classes.card} radius="md" value={item.name} key={item.name} data-testid={`${item.name} radio button`}>
            <Group wrap="nowrap" align="flex-start">
                <Radio.Indicator/>
                <div>
                    <Text fw={'bold'}>{item.name}</Text>
                    {item.image &&
                        <Image src={item.image}/>
                    }
                </div>
            </Group>
        </Radio.Card>
    ));
    return (
        <Modal opened={true} onClose={close} centered size={'40%'} withCloseButton={false}>
            <Title order={2} mb={'md'}>Initial supply-chain layout</Title>
            <Radio.Group
                value={value}
                onChange={setValue}
                label="Create an initial layout for designing the supply-chain network."
            >
                <SimpleGrid cols={2} pt={'sm'}>
                    {cards}
                </SimpleGrid>
            </Radio.Group>
            <Text size={'md'} p={'2em 0'}>
                Note: The network can be modified (adding, removing or moving players) in the next screen.
            </Text>
            <Group justify={'right'}>
                <Button onClick={() => value && applyDesignModeState(value)} disabled={!value} data-testid={'createNetworkButton'}>
                    Create initial network
                </Button>
                <Button onClick={close} variant={'outline'}>Cancel</Button>
            </Group>
        </Modal>
    );
}
